import React, { useEffect, useState, useMemo } from 'react';
import { Col, Row, Card, CardBody, Collapse, Button, CardTitle } from 'reactstrap';
import { unitTypes } from '@sparklib';
import TabList from './TabList';
import Slider from '../../../components/Slider';
import SmallMap from '../../Recommendation/SmallMap';
import { Icon } from '@assets';
import { PROPERTY, PROPERTY_IMAGES, UPDATE_USER_PROPERTY_NOTES, CREATE_USER_PROPERTY_NOTES } from '@api';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import ScrollToTopButton from './ScrollToTopButton';
import { toast } from 'react-toastify';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';

// Simple skeleton shimmer element for fields that haven't loaded yet
function Skeleton({ width = '120px', inline = false }) {
  return (
    <span
      className='skeleton-shimmer'
      style={{
        display: inline ? 'inline-block' : 'block',
        width,
        height: '1em',
        borderRadius: '4px',
        background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.4s infinite',
        verticalAlign: 'middle',
      }}
    />
  );
}

function groupFloorPlansArray(floorPlans) {
  return floorPlans.reduce((groups, fp) => {
    (groups[fp.bed] = groups[fp.bed] || []).push(fp);
    return groups;
  }, {});
}

export default function ShowProperty(props) {
  // initialData comes from the fast-properties list (already on client).
  // It has a subset of fields — we render immediately and enrich in background.
  const { initialData } = props;

  const [property, setProperty] = useState(() => initialData || null);
  const [imageUrls, setImageUrls] = useState(null); // full-size, loaded separately
  const [sliderLazy, setSliderLazy] = useState(true); // false once full images are preloaded
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(true);

  // Note editor state
  const [selfNotes, setSelfNotes] = useState('');
  const [agentNotes, setAgentNotes] = useState('');
  const [selfNoteId, setSelfNoteId] = useState(null);
  const [agentNoteId, setAgentNoteId] = useState(null);
  const [isAgentEditable, setIsAgentEditable] = useState(false);
  const [isSelfEditable, setIsSelfEditable] = useState(false);

  // Collapsibles (mobile)
  const [isOpen, setIsOpen] = useState(false);
  const [details, setDetails] = useState(false);

  // When a completely different property is passed via props.property (non-fast path)
  useEffect(() => {
    if (props.property) {
      setProperty(props.property);
      setDetailsLoading(false);
    }
  }, [props.property]);

  // When initialData changes (user clicks a different row), reset state
  useEffect(() => {
    if (initialData) {
      setProperty(initialData);
      setImageUrls(null);
      setSliderLazy(true);
      setDetailsLoading(true);
      setImagesLoading(true);
      setSelfNotes('');
      setAgentNotes('');
      setSelfNoteId(null);
      setAgentNoteId(null);
    }
  }, [initialData?.id]);

  // The route is /properties/:id (legacy) or
  // /properties/:city_name/:neighborhood_name/:slug (current — both card-click
  // and share-link paths). Prefer id when we have it (in-memory from list/card
  // click data) so the backend hits the primary key; fall back to slug only
  // when arriving directly via URL with no id.
  //
  // Locked to initialData?.id (not property?.id) so we don't switch keys mid-
  // session — once PROPERTY returns and seeds property.id, we don't refire.
  const { id: routeId, slug: routeSlug } = useParams();
  const lookupVars = useMemo(() => {
    if (initialData?.id) return { id: String(initialData.id), slug: null };
    if (routeId)         return { id: String(routeId),        slug: null };
    if (routeSlug)       return { id: null, slug: routeSlug };
    return null;
  }, [initialData?.id, routeId, routeSlug]);

  // ── Background fetch: property details (fast — no imageUrls) ──────────────
  useQuery(PROPERTY, {
    variables: { ...(lookupVars || {}), ...props.queryVariables },
    skip: props.property || !lookupVars,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.property) {
        setProperty(prev => ({ ...prev, ...data.property }));
        // Seed note editors from embedded notes
        const sn = data.property.selfNote;
        const an = data.property.agentNote;
        if (sn) { setSelfNotes(sn.notes || ''); setSelfNoteId(Number(sn.id)); }
        if (an) { setAgentNotes(an.notes || ''); setAgentNoteId(Number(an.id)); }
        setDetailsLoading(false);
      }
    },
  });

  // ── Background fetch: full-size images (slow on cold cache) ───────────────
  useQuery(PROPERTY_IMAGES, {
    variables: lookupVars || {},
    skip: !lookupVars,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const urls = data?.property?.imageUrls;
      if (!urls?.length) { setImagesLoading(false); return; }
      // Preload all full images so the browser caches them before we swap the slider.
      // Once ready, switch to <img> (isLazyLoad=false) so the swap is instant — no placeholder flash.
      let remaining = urls.length;
      urls.forEach(url => {
        const img = new window.Image();
        img.onload = img.onerror = () => {
          remaining--;
          if (remaining === 0) {
            setSliderLazy(false);
            setImageUrls(urls);
            setImagesLoading(false);
          }
        };
        img.src = url;
      });
    },
    onError: () => setImagesLoading(false),
  });

  // ── Note mutations ─────────────────────────────────────────────────────────
  const [updateUserPropertyNote] = useMutation(UPDATE_USER_PROPERTY_NOTES, {
    onCompleted: (data) => {
      if (data.updateUserPropertyNote.message) {
        toast(data.updateUserPropertyNote.message, { type: 'success' });
      }
      if (data.updateUserPropertyNote.errors?.length > 0) {
        data.updateUserPropertyNote.errors.forEach(err => toast(err, { type: 'error' }));
      }
    },
    onError: (e) => console.log(e),
  });

  const [createUserPropertyNote] = useMutation(CREATE_USER_PROPERTY_NOTES, {
    onCompleted: (data) => {
      if (data.createUserPropertyNote.message) {
        toast(data.createUserPropertyNote.message, { type: 'success' });
      }
      if (data.createUserPropertyNote.errors?.length > 0) {
        data.createUserPropertyNote.errors.forEach(err => toast(err, { type: 'error' }));
      }
    },
    onError: (e) => console.log(e),
  });

  const handleSaveNote = (noteType) => {
    const noteId = noteType === 'for_self' ? selfNoteId : agentNoteId;
    const notes  = noteType === 'for_self' ? selfNotes  : agentNotes;
    if (noteId) {
      updateUserPropertyNote({ variables: { id: noteId, notes } });
    } else {
      createUserPropertyNote({
        variables: { propertyId: String(property.id), notes, noteType },
      });
    }
  };

  // ── Derived display values ─────────────────────────────────────────────────
  const groupedFloorPlans = property ? groupFloorPlansArray(property.floorPlans || []) : {};
  const defaultTab = Object.keys(unitTypes).find(type => groupedFloorPlans[type]);
  const disabledTab = Object.keys(unitTypes).map(type => !groupedFloorPlans[type]);
  const allTabsDisabled = disabledTab.every(Boolean);

  const [activeTab, setActiveTab] = useState(unitTypes[defaultTab] || '');
  useEffect(() => {
    const gfp = property ? groupFloorPlansArray(property.floorPlans || []) : {};
    const dt = Object.keys(unitTypes).find(type => gfp[type]);
    setActiveTab(unitTypes[dt] || '');
  }, [property?.id]);

  // Slider images: prefer full-size; fall back to small thumbnails while loading
  const sliderImages = (() => {
    const urls = imageUrls || property?.imageSmallUrls || property?.imageUrls || [];
    return urls.map(src => ({ src, altText: property?.name || '' }));
  })();

  const renderPolicyButton = (policyItem) => {
    if (property?.policyItems?.includes(policyItem)) {
      return (
        <button key={policyItem} className='btn btn-outline-danger btn-sm mx-2 my-2' disabled>
          {policyItem}
        </button>
      );
    }
  };

  // ── Skeleton-aware field renderer ──────────────────────────────────────────
  // Returns value if present; skeleton shimmer if still loading; '—' if loaded but absent
  const field = (value, skeletonWidth = '160px') => {
    if (value != null && value !== '') return value;
    if (detailsLoading) return <Skeleton width={skeletonWidth} inline />;
    return '—';
  };

  const linkField = (href, label, icon) => {
    if (href) {
      return (
        <p className='mr-3 d-inline'>
          <Icon icon={icon} />
          <a href={href} target='_blank' rel='noreferrer'>{label}</a>
        </p>
      );
    }
    if (detailsLoading) return <p className='mr-3 d-inline'><Skeleton width='80px' inline /></p>;
    return null;
  };

  if (!property) {
    return (
      <div className='p-4 edit-popup' style={{ marginTop: '35%', marginLeft: '45%' }}>
        <h4>Loading ...</h4>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <div className='p-4 edit-popup'>
        <Row>
          <Col md='12' className='d-md-flex align-items-center mb-3'>
            <div style={{ position: 'relative', width: '100%' }}>
              <Slider
                items={sliderImages}
                className='slider rounded-medium'
                isLazyLoad={sliderLazy}
                propertyId={property.id}
              />
              {imagesLoading && (imageUrls === null) && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '12px',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    pointerEvents: 'none',
                  }}
                >
                  Loading full photos…
                </div>
              )}
            </div>
          </Col>
        </Row>

        <Row>
          {/* Property name + policy badges */}
          <Col md='12' className='d-md-flex align-items-center mb-3'>
            <h3 className='d-inline-block mb-md-0 mr-4'>
              {property.name} <small>({property.neighborhood?.name})</small>
            </h3>
            {['45+', '55+', '60+', '62+', 'Accepts Section 8', 'Tax Credit Property', 'Income Restrictions'].map(renderPolicyButton)}
          </Col>

          {property.specials?.trim().length > 1 && (
            <Col lg='12'>
              <p className='p-2' style={{ background: 'var(--silver)', borderRadius: 'var(--rounded-small)' }}>
                <Icon icon='tag' /> Specials: {property.specials}
              </p>
            </Col>
          )}

          {/* ── Mobile collapsible: details ── */}
          <Col md='12'>
            <div className='d-block d-sm-none'>
              <Button color='light' className='my-3 mt-4 btn-rounded btn btn-md float-right' onClick={() => setDetails(d => !d)}>
                {details ? 'Collapse' : 'Expand Full Details'}&nbsp;
                <Icon icon={details ? 'chevron-up' : 'chevron-down'} />
              </Button>
              <Collapse isOpen={details}>
                <Card className='w-100'>
                  <CardBody>
                    <p><Icon icon='map-marker-alt' /> {field(property.address, '200px')}</p>
                    <p><Icon icon='envelope' /> {field(property.email, '180px')}</p>
                    {property.secondaryEmail && <p><Icon icon='envelope' /> <a href={`mailto:${property.secondaryEmail}`} target='_blank' rel='noreferrer'>{property.secondaryEmail}</a></p>}
                    {property.otherEmail && <p><Icon icon='envelope' /> <a href={`mailto:${property.otherEmail}`} target='_blank' rel='noreferrer'>{property.otherEmail}</a></p>}
                    <p><Icon icon='phone-alt' /> {field(property.phone, '120px')}</p>
                    <p><Icon icon='calendar-alt' /> Built Year: {field(property.builtYear, '60px')}</p>
                    <p><Icon icon='calendar-alt' /> Renovated: {field(property.renovated, '60px')}</p>
                    <div className='pb-3'>{linkField(property.webLink, 'Website', 'globe')}</div>
                    <div className='pb-3'>{linkField(property.googleMap, 'Map', 'map-marked-alt')}</div>
                    <p><Icon icon='building' /> Management Co.: {field(property.managementCompany, '160px')}</p>
                    <p><Icon icon='shield-alt' /> Escort %: {field(property.escort, '60px')}</p>
                    <p><Icon icon='shield-alt' /> Send %: {field(property.sendEscort, '60px')}</p>
                    <p><Icon icon='shield-alt' /> Bonus: {field(property.bonus, '80px')}</p>
                    {linkField(property.googleReviewLink, 'Review', 'star')}
                    {linkField(property.photoGalleryLink, 'Gallery', 'images')}
                    {linkField(property.floorPlanLink, 'Floor Plan', 'home')}
                    {linkField(property.virtualTourLink, 'Virtual Tour', 'video')}
                    {linkField(property.feesSheetUrl, 'Fee Sheet', 'dollar-sign')}
                    {linkField(property.siteMapUrl, 'Site Map', 'map')}
                  </CardBody>
                </Card>
              </Collapse>
            </div>
          </Col>

          {/* ── Mobile collapsible: notes ── */}
          <Col md='12'>
            <div className='d-block d-sm-none'>
              <Button color='light' className='my-3 mt-4 btn-rounded btn btn-md float-right' onClick={() => setIsOpen(o => !o)}>
                {isOpen ? 'Collapse Notes' : 'Expand Notes'}&nbsp;
                <Icon icon={isOpen ? 'chevron-up' : 'chevron-down'} />
              </Button>
              <Collapse isOpen={isOpen}>
                {renderNoteCards()}
              </Collapse>
            </div>
          </Col>

          {/* ── Desktop: links row ── */}
          <Col lg='4'><div className='d-none d-sm-block pb-3'>
            {linkField(property.webLink, 'Website', 'globe')}
            {linkField(property.googleMap, 'Map', 'map-marked-alt')}
          </div></Col>

          <Col lg='4'><div className='d-none d-sm-block'>
            <p>
              <Icon icon='map-marker-alt' />{' '}
              {property.address
                ? <a href={`add:${property.address}`} target='_blank' rel='noreferrer'>{property.address}</a>
                : field(property.address)}
            </p>
          </div></Col>

          <Col lg='4'><div className='d-none d-sm-block'>
            <p>
              <Icon icon='phone-alt' />{' '}
              {property.phone
                ? <a href={`tel:${property.phone}`} target='_blank' rel='noreferrer'>{property.phone}</a>
                : field(property.phone)}
            </p>
          </div></Col>

          <Col lg='4'><div className='d-none d-sm-block'>
            <p>
              <Icon icon='envelope' />{' '}
              {property.email
                ? <a href={`mailto:${property.email}`} target='_blank' rel='noreferrer'>{property.email}</a>
                : field(property.email)}
            </p>
            {property.secondaryEmail && <p><Icon icon='envelope' /> <a href={`mailto:${property.secondaryEmail}`} target='_blank' rel='noreferrer'>{property.secondaryEmail}</a></p>}
            {property.otherEmail && <p><Icon icon='envelope' /> <a href={`mailto:${property.otherEmail}`} target='_blank' rel='noreferrer'>{property.otherEmail}</a></p>}
          </div></Col>

          <Col lg='4'><div className='d-none d-sm-block'>
            <p><Icon icon='building' /> Management Co.: {field(property.managementCompany)}</p>
          </div></Col>

          <Col lg='4'><div className='d-none d-sm-block'>
            <p><Icon icon='calendar-alt' /> Built Year: {property.builtYear ?? field(property.builtYear, '50px')}</p>
          </div></Col>

          <Col lg='4'><div className='d-none d-sm-block'>
            <p><Icon icon='calendar-alt' /> Renovated: {property.renovated ?? field(property.renovated, '50px')}</p>
          </div></Col>

          <Col lg='4'><div className='d-none d-sm-block'>
            <p><Icon icon='shield-alt' /> Escort %: {property.escort ?? field(property.escort, '50px')}</p>
          </div></Col>

          <Col lg='4'><div className='d-none d-sm-block'>
            <p><Icon icon='shield-alt' /> Send %: {property.sendEscort ?? field(property.sendEscort, '50px')}</p>
          </div></Col>

          <Col lg='4'><div className='d-none d-sm-block'>
            <p><Icon icon='shield-alt' /> Bonus: {property.bonus ?? field(property.bonus, '80px')}</p>
          </div></Col>

          <Col lg='4'><div className='d-none d-sm-block'>{linkField(property.googleReviewLink, 'Review', 'star')}</div></Col>
          <Col lg='4'><div className='d-none d-sm-block'>{linkField(property.photoGalleryLink, 'Gallery', 'images')}</div></Col>
          <Col lg='4'><div className='d-none d-sm-block'>{linkField(property.floorPlanLink, 'Floor Plan', 'home')}</div></Col>
          <Col lg='4'><div className='d-none d-sm-block'>{linkField(property.virtualTourLink, 'Virtual Tour', 'video')}</div></Col>
          <Col lg='4'><div className='d-none d-sm-block'>{linkField(property.feesSheetUrl, 'Fee Sheet', 'dollar-sign')}</div></Col>
          <Col lg='4'><div className='d-none d-sm-block'>{linkField(property.siteMapUrl, 'Site Map', 'map')}</div></Col>
          <Col lg='4'><div className='d-none d-sm-block'>{linkField(property.essentialHousing, 'Essential Housing', 'building')}</div></Col>

          {/* Notes + metadata — visible on desktop, wait for details */}
          {property.schedulingNote?.length > 5 && (
            <Col lg='12'><div className='d-none d-sm-block mt-2'>
              <p><strong>Scheduling Note:</strong><span className='ml-2'>{property.schedulingNote}</span></p>
            </div></Col>
          )}
          {property.commissionNote?.length > 5 && (
            <Col lg='12'><div className='d-none d-sm-block mt-2'>
              <p><strong>Commission Note:</strong><span className='ml-2'>{property.commissionNote}</span></p>
            </div></Col>
          )}
          {property.addonItems?.length > 0 && (
            <Col lg='12'><div className='d-none d-sm-block'>
              <p><strong>Community Features / Amenities:</strong><span className='ml-2'>{property.addonItems.join(', ')}</span></p>
            </div></Col>
          )}
          {property.unitsUpdatedAt?.length > 5 && (
            <Col lg='12'><div className='d-none d-sm-block mt-2'>
              <p>
                <strong>Pricing Last Updated:</strong>
                <span className='ml-2'>
                  {allTabsDisabled
                    ? 'Pricing and availability for this community is not currently available.'
                    : moment(property.unitsUpdatedAt).fromNow()}
                </span>
              </p>
            </div></Col>
          )}
          {property.policyItems?.length > 0 && (
            <Col lg='12'><div className='d-none d-sm-block'>
              <p><strong>Community Policies:</strong><span className='ml-2'>{property.policyItems.join(', ')}</span></p>
            </div></Col>
          )}
          {property.virtualTours?.length > 5 && (
            <Col lg='12'><div className='d-none d-sm-block'>
              <p><strong>Virtual Tour:</strong><span className='ml-2'>{property.virtualTours}</span></p>
            </div></Col>
          )}
          {property.parkingNote?.length > 5 && (
            <Col lg='12'><div className='d-none d-sm-block'>
              <p><strong>Parking Note:</strong><span className='ml-2'>{property.parkingNote}</span></p>
            </div></Col>
          )}
          {property.invoicingNote?.length > 5 && (
            <Col lg='12'><div className='d-none d-sm-block'>
              <p><strong>Invoicing Note:</strong><span className='ml-2'>{property.invoicingNote}</span></p>
            </div></Col>
          )}
          {property.additionalNote && (
            <Col lg='12'><div className='d-none d-sm-block'>
              <p><strong>Additional Note:</strong><span className='ml-2'>{property.additionalNote}</span></p>
            </div></Col>
          )}
          {property.policyNote && (
            <Col lg='12'><div className='d-none d-sm-block'>
              <p><strong>Policy Note:</strong><span className='ml-2'>{property.policyNote}</span></p>
            </div></Col>
          )}

          {/* ── Broker / Agent notes (only after details load) ── */}
          {!detailsLoading && localStorage.getItem('is_broker') === 'true' && (
            <Col lg='12'>
              <div className='mb-2'>
                <Button id='brokerNotesCollapse' style={{ width: '250px' }} type='button' outline color='secondary'>
                  <Icon icon='comment' className='mr-2' />
                  Notes (For your agents)
                  <Icon icon='pen' className='ml-3' style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setIsAgentEditable(true); }} />
                </Button>
                <div className='theme-collapse custom-collapse'>
                  {isAgentEditable ? (
                    <div className='custom-editor'>
                      <SunEditor
                        setOptions={{ showPathLabel: false, defaultStyle: 'width: 100%; min-height: 200px', buttonList: [['undo', 'redo'], ['bold', 'underline', 'italic'], ['list', 'link', 'removeFormat']] }}
                        setContents={agentNotes}
                        onChange={setAgentNotes}
                      />
                    </div>
                  ) : (
                    <Card className='custom-card'><CardBody><div dangerouslySetInnerHTML={{ __html: agentNotes }} /></CardBody></Card>
                  )}
                  {isAgentEditable && (
                    <Button type='button' onClick={() => { handleSaveNote('for_agents'); setIsAgentEditable(false); }} className='sm mt-2' color='primary'>
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </Col>
          )}

          {!detailsLoading && localStorage.getItem('is_agent') === 'true' && (
            <Col lg='12'>
              <div className='mb-2'>
                <Button id='agentBrokerNotes' style={{ width: '250px' }} type='button' outline color='secondary'>
                  <Icon icon='comment' className='mr-2' />
                  Notes (From your broker)
                </Button>
                <div className='theme-collapse custom-collapse'>
                  <Card className='custom-card'><CardBody>
                    {agentNotes ? <div dangerouslySetInnerHTML={{ __html: agentNotes }} /> : <p>No notes available.</p>}
                  </CardBody></Card>
                </div>
              </div>
            </Col>
          )}

          {!detailsLoading && (['user', 'true'].includes(localStorage.getItem('user_type')) || localStorage.getItem('is_broker') === 'true' || localStorage.getItem('is_agent') === 'true') && (
            <Col lg='12'>
              <div className='mb-2'>
                <Button id='notesCollapse' style={{ width: '250px' }} type='button' outline color='secondary'>
                  <Icon icon='comment' className='mr-2' />
                  <span className='mr-2'>Notes (Private to you)</span>
                  <Icon icon='pen' className='ml-3' style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setIsSelfEditable(true); }} />
                </Button>
                <div className='theme-collapse custom-collapse'>
                  {isSelfEditable ? (
                    <div className='custom-editor'>
                      <SunEditor
                        setOptions={{ showPathLabel: false, defaultStyle: 'width: 100%; min-height: 200px', buttonList: [['undo', 'redo'], ['bold', 'underline', 'italic'], ['list', 'link', 'removeFormat']] }}
                        setContents={selfNotes}
                        onChange={setSelfNotes}
                      />
                    </div>
                  ) : (
                    <Card className='custom-card'><CardBody><div dangerouslySetInnerHTML={{ __html: selfNotes }} /></CardBody></Card>
                  )}
                  {isSelfEditable && (
                    <Button type='button' onClick={() => { handleSaveNote('for_self'); setIsSelfEditable(false); }} className='sm mt-2 mb-4' color='primary'>
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </Col>
          )}

          {/* ── Floor plan tabs ── */}
          <Col md='12' className='table-tabs'>
            <TabList
              unitTypes={unitTypes}
              property={property}
              detailsLoading={detailsLoading}
              selectedProperties={props.selectedProperties}
              setSelectedProperties={props.setSelectedProperties}
              pageName='showProperty'
              activeFilters={props.activeFilters}
            />
          </Col>

          <ScrollToTopButton />

          {/* ── Map (only once lat/long available) ── */}
          {(property.lat && property.long) ? (
            <Col md='12'>
              <Card className='theme-card mt-3'>
                <CardBody className='p-0' style={{ height: '450px' }}>
                  <SmallMap
                    property_ids={[property.id]}
                    height='450px'
                    showTooltip={false}
                    latlongs={[{ lat: property.lat, long: property.long, id: property.id, property }]}
                    property={property}
                    is_show={true}
                    loadingElement={<div style={{ height: '100%' }} />}
                    containerElement={<div style={{ height: '100%' }} />}
                    mapElement={<div style={{ height: '100%' }} />}
                  />
                </CardBody>
              </Card>
            </Col>
          ) : detailsLoading ? (
            <Col md='12'>
              <div className='skeleton-shimmer mt-3' style={{
                height: '200px',
                borderRadius: '8px',
                background: 'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.4s infinite',
              }} />
            </Col>
          ) : null}
        </Row>
      </div>
    </>
  );

  function renderNoteCards() {
    return (
      <>
        {property.schedulingNote?.length > 5 && (
          <Card className='w-100'><CardBody><CardTitle tag='h5'>Scheduling Note:</CardTitle>{property.schedulingNote}</CardBody></Card>
        )}
        {property.commissionNote?.length > 5 && (
          <Card className='w-100 mt-2'><CardBody><CardTitle tag='h5'>Commission Note:</CardTitle>{property.commissionNote}</CardBody></Card>
        )}
        {property.addonItems?.length > 0 && (
          <Card className='w-100 mt-2'><CardBody><CardTitle tag='h5'>Community Features / Amenities:</CardTitle>{property.addonItems.join(', ')}</CardBody></Card>
        )}
        {property.policyItems?.length > 0 && (
          <Card className='w-100 mt-2'><CardBody><CardTitle tag='h5'>Community Policies:</CardTitle>{property.policyItems.join(', ')}</CardBody></Card>
        )}
        {property.virtualTours?.length > 5 && (
          <Card className='w-100 mt-2'><CardBody><CardTitle tag='h5'>Virtual Tour:</CardTitle>{property.virtualTours}</CardBody></Card>
        )}
        {property.parkingNote?.length > 5 && (
          <Card className='w-100 mt-2'><CardBody><CardTitle tag='h5'>Parking Note:</CardTitle>{property.parkingNote}</CardBody></Card>
        )}
        {property.invoicingNote?.length > 5 && (
          <Card className='w-100 mt-2'><CardBody><CardTitle tag='h5'>Invoicing Note:</CardTitle>{property.invoicingNote}</CardBody></Card>
        )}
        {property.additionalNote && (
          <Card className='w-100 mt-2'><CardBody><CardTitle tag='h5'>Additional Note:</CardTitle>{property.additionalNote}</CardBody></Card>
        )}
        {property.policyNote && (
          <Card className='w-100 mt-2'><CardBody><CardTitle tag='h5'>Policy Note:</CardTitle>{property.policyNote}</CardBody></Card>
        )}
        {property.unitsUpdatedAt && (
          <Card className='w-100 mt-2'><CardBody><CardTitle tag='h5'>Pricing Last Updated:</CardTitle>
            {allTabsDisabled ? 'Pricing and availability for this community is not currently available.' : moment(property.unitsUpdatedAt).fromNow()}
          </CardBody></Card>
        )}
      </>
    );
  }
}
